import type { LandingKollega } from '@ertekbecslo/kozos/iroda';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import KorzetOlvaso from '../korzet/KorzetOlvaso.tsx';
import { fotoKicsinyites } from '../kollegak/fotoKicsinyites.ts';
import { atrendezes, kollegaHiba, kollegakNormalizalasa, MAX_KOLLEGA } from '../kollegak/weboldalKollegak.ts';
import { supabase } from '../lib/supabase.ts';
import { Betolto, Fokonyv, Gomb, HibaUzenet, Mezo, OldalFej, Ures } from '../ui/elemek.tsx';
import NincsOldal from './NincsOldal.tsx';

const TAROLO = 'kollega-fotok';

/** A nyilvános URL-ből a tárolón belüli útvonal (`{office}/{uuid}.webp`), vagy null, ha nem a mi tárolónk képe. */
function fotoUt(url: string | undefined): string | null {
  const jel = `/object/public/${TAROLO}/`;
  const i = url?.indexOf(jel) ?? -1;
  return url && i >= 0 ? decodeURIComponent(url.slice(i + jel.length)) : null;
}

export default function Weboldal() {
  const { jogok, aktivId, irodak } = useAktivIroda();
  const iroda = irodak.find((i) => i.id === aktivId);
  const queryClient = useQueryClient();
  const betoltes = useQuery({
    queryKey: ['weboldal-kollegak', aktivId],
    enabled: jogok.vezeto && aktivId !== null,
    gcTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase.from('offices').select('landing_tartalom').eq('id', aktivId!).single();
      if (error) throw error;
      return kollegakNormalizalasa(data.landing_tartalom);
    },
  });
  const [lista, setLista] = useState<LandingKollega[]>([]);
  const [eredeti, setEredeti] = useState<LandingKollega[]>([]);
  const [hiba, setHiba] = useState<unknown>(null);
  const [mezoHibak, setMezoHibak] = useState<(string | null)[]>([]);
  const [siker, setSiker] = useState(false);
  const [mentes, setMentes] = useState(false);
  const [feltoltes, setFeltoltes] = useState<number | null>(null);

  // A szerverről érkező listával csak első betöltéskor és irodaváltáskor töltünk fel, hogy a mentetlen szerkesztés ne vesszen el fülváltáskor.
  const feltoltottIroda = useRef<string | null>(null);
  useEffect(() => {
    if (betoltes.data && feltoltottIroda.current !== aktivId) {
      feltoltottIroda.current = aktivId;
      setLista(betoltes.data); setEredeti(betoltes.data); setMezoHibak([]);
    }
  }, [betoltes.data, aktivId]);

  if (!jogok.vezeto) return <NincsOldal />;
  if (!aktivId) return <Ures cim="Nincs kiválasztott iroda" />;

  const modosit = (i: number, resz: Partial<LandingKollega>) => {
    setSiker(false);
    setLista((l) => l.map((k, j) => (j === i ? { ...k, ...resz } : k)));
  };

  async function fotoValasztas(i: number, fajl: File | undefined) {
    if (!fajl) return;
    setHiba(null);
    setFeltoltes(i);
    try {
      const blob = await fotoKicsinyites(fajl);
      const ut = `${aktivId}/${crypto.randomUUID()}.webp`;
      const { error } = await supabase.storage.from(TAROLO).upload(ut, blob, { contentType: 'image/webp', cacheControl: '31536000', upsert: false });
      if (error) throw error;
      modosit(i, { fotoUrl: supabase.storage.from(TAROLO).getPublicUrl(ut).data.publicUrl });
    } catch (e) {
      setHiba(e);
    } finally {
      setFeltoltes(null);
    }
  }

  async function mentesInditas() {
    setHiba(null);
    setSiker(false);
    const hibak = lista.map(kollegaHiba);
    setMezoHibak(hibak);
    if (hibak.some((h) => h !== null)) return;
    setMentes(true);
    try {
      const tiszta = lista.map((k) => ({
        nev: k.nev.trim(),
        ...((k.szerep ?? '').trim() ? { szerep: k.szerep!.trim() } : {}),
        ...(k.fotoUrl ? { fotoUrl: k.fotoUrl } : {}),
      }));
      const { error } = await supabase.rpc('iroda_kollegak_mentes', { p_office_id: aktivId!, p_kollegak: tiszta });
      if (error) throw error;
      // A mentés után már nem használt fotókat töröljük a tárolóból.
      const marad = new Set(tiszta.map((k) => fotoUt(k.fotoUrl)).filter(Boolean));
      const torlendo = eredeti.map((k) => fotoUt(k.fotoUrl)).filter((u): u is string => u !== null && !marad.has(u));
      if (torlendo.length > 0) {
        const { error: torlesHiba } = await supabase.storage.from(TAROLO).remove(torlendo);
        // Csak tárhely marad foglalva, a mentés sikeres, ezért nem dobunk hibát a felhasználónak.
        if (torlesHiba) console.warn('A régi kolléga-fotók törlése nem sikerült', torlesHiba);
      }
      queryClient.setQueryData(['weboldal-kollegak', aktivId], tiszta);
      setEredeti(tiszta);
      setLista(tiszta);
      setSiker(true);
    } catch (e) {
      setHiba(e);
    } finally {
      setMentes(false);
    }
  }

  const alap = import.meta.env.VITE_PUBLIKUS_ALAP as string | undefined;
  return (
    <div className="grid max-w-3xl gap-5">
      <OldalFej cim="Kollégáink a weboldalon" leiras="Itt adhatod meg, kik jelenjenek meg az iroda oldalán. Csak fotó, név és szerep jelenik meg, elérhetőség soha.">
        {iroda && alap && (
          <a href={`${alap}/${iroda.slug}`} target="_blank" rel="noreferrer" className="inline-flex min-h-10 w-fit shrink-0 items-center whitespace-nowrap rounded-xl border border-vonal-eros px-4 text-sm hover:bg-felulet">Az iroda oldalának megnyitása</a>
        )}
      </OldalFej>

      <HibaUzenet hiba={betoltes.error} />
      {betoltes.isPending && <Betolto />}
      {betoltes.data && (
        <>
          {lista.length === 0 && <Ures cim="Még nincs kolléga az oldalon" szoveg="Add hozzá az első munkatársat." />}
          <Fokonyv>
            {lista.map((k, i) => (
              <section key={i} className="px-5 py-5">
                <div className="grid gap-3">
                  <div className="flex items-center gap-3">
                    {k.fotoUrl
                      ? <img src={k.fotoUrl} alt="" className="size-16 shrink-0 rounded-full object-cover" />
                      : <div aria-hidden className="grid size-16 shrink-0 place-items-center rounded-full bg-felulet-2 text-sm text-szoveg-halk">Fotó</div>}
                    <div className="grid min-w-0 gap-1">
                      <input id={`foto-${i}`} type="file" accept="image/*" disabled={feltoltes !== null}
                        onChange={(e) => { void fotoValasztas(i, e.target.files?.[0]); e.target.value = ''; }}
                        className="peer sr-only text-[max(16px,1rem)]" />
                      <label htmlFor={`foto-${i}`}
                        className={`inline-flex min-h-10 w-fit items-center rounded-xl border border-vonal-eros px-4 text-sm peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-szoveg ${feltoltes !== null ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-felulet'}`}>
                        {feltoltes === i ? 'Feltöltés…' : k.fotoUrl ? 'Fotó cseréje' : 'Fotó kiválasztása'}
                      </label>
                    </div>
                  </div>
                  <Mezo label="Név" value={k.nev} hiba={mezoHibak[i]} onChange={(e) => modosit(i, { nev: e.target.value })} />
                  <Mezo label="Szerep" value={k.szerep ?? ''} onChange={(e) => modosit(i, { szerep: e.target.value })} placeholder="pl. értékesítő" />
                  <div className="flex flex-wrap gap-2">
                    <Gomb valtozat="masodlagos" aria-label={`${k.nev || 'Kolléga'} feljebb`} disabled={i === 0}
                      onClick={() => { setSiker(false); setLista((l) => atrendezes(l, i, -1)); }}>Feljebb</Gomb>
                    <Gomb valtozat="masodlagos" aria-label={`${k.nev || 'Kolléga'} lejjebb`} disabled={i === lista.length - 1}
                      onClick={() => { setSiker(false); setLista((l) => atrendezes(l, i, 1)); }}>Lejjebb</Gomb>
                    <Gomb valtozat="veszely" onClick={() => { setSiker(false); setLista((l) => l.filter((_, j) => j !== i)); }}>Törlés</Gomb>
                  </div>
                </div>
              </section>
            ))}
          </Fokonyv>
          <p className="text-sm text-szoveg-halk">
            Ezek a kollégák csak a weboldalon látszanak. A leadeket a <Link to="/kollegak" className="text-kiemelt underline">Kollégák oldalon</Link> meghívott értékesítők kapják.
          </p>
          <div className="flex flex-wrap gap-2">
            <Gomb valtozat="masodlagos" disabled={lista.length >= MAX_KOLLEGA}
              onClick={() => { setSiker(false); setLista((l) => [...l, { nev: '' }]); }}>Kolléga hozzáadása</Gomb>
            <Gomb toltes={mentes} onClick={() => void mentesInditas()}>Mentés</Gomb>
          </div>
          {lista.length >= MAX_KOLLEGA && <p className="text-sm text-szoveg-halk">Legfeljebb {MAX_KOLLEGA} kolléga jeleníthető meg.</p>}
          <HibaUzenet hiba={hiba} />
          {siker && <p role="status" className="rounded-xl border border-siker px-3.5 py-2.5 text-sm text-siker">Mentve. Az oldalon pár percen belül frissül.</p>}
        </>
      )}

      <KorzetOlvaso officeId={aktivId} />
    </div>
  );
}
